import MDEditor from '@uiw/react-md-editor';
import { useFormik } from 'formik';
import { Badge, Button, Spoiler } from '@mantine/core';
import type { TAttribute, TAttributeData } from '@newo/api/gateway';
import { cn } from '@/utils';
// mantine
// components
import AttributeValue from '@/pages/Attributes/components/AttributeValue';
import ContextMenu from '@/pages/Attributes/components/Attribute/components/ContextMenu';
import VibeConsultationButton from '@/modules/VibeMode/components/VibeConsultationButton';

// utils
import validationSchema from '@/pages/Attributes/components/AttributeForm/AttributeForm.schema.ts';
import { VibeConsultationType } from '@/modules/VibeMode/vibe-mode-dock.contract';

interface AttributeProps {
    attribute: TAttribute;
    withVibeConsultation?: boolean;
    onDelete: (attribute: TAttribute) => void;
    onEdit: (attribute: TAttribute) => void;
    onSave: ({
        attributeId,
        data
    }: {
        attributeId?: string;
        data: TAttributeData;
    }) => Promise<void>;
}

const Attribute = ({
    attribute,
    withVibeConsultation = false,
    onDelete,
    onEdit,
    onSave
}: AttributeProps) => {
    const { id, ...rest } = attribute;
    const initialValues: TAttributeData = {
        ...rest,
        value: attribute.value
    };

    const formik = useFormik({
        initialValues,
        validationSchema,
        enableReinitialize: true,
        async onSubmit(formData) {
            await onSave({
                attributeId: id,
                data: formData
            });
        }
    });

    const handleDelete = () => {
        onDelete(attribute);
    };

    const handleEdit = () => {
        onEdit(attribute);
    };

    const draftValue = formik.values.value;
    const isChanged = draftValue !== attribute.value;

    return (
        <article
            className={cn(
                'flex flex-col gap-2 relative rounded-lg bg-gray-2 p-4 border-2 border-solid border-gray-2',
                {
                    'bg-white': attribute.is_hidden
                }
            )}
        >
            <div className="flex gap-4">
                <div className="flex-1">
                    {attribute.title ? (
                        <>
                            <span className="font-semibold">
                                {attribute.title}
                            </span>{' '}
                            <span className="text-gray-4">/</span>{' '}
                        </>
                    ) : undefined}

                    <span className="text-blue-7 text-xs">{attribute.idn}</span>
                </div>
                {attribute.is_hidden ? (
                    <Badge color="gray" variant="outline">
                        HIDDEN
                    </Badge>
                ) : undefined}
                {withVibeConsultation ? (
                    <VibeConsultationButton
                        attributeIdn={attribute.idn}
                        consultationType={VibeConsultationType.Attributes}
                        draftValue={draftValue}
                        isChanged={isChanged}
                    />
                ) : undefined}
                <ContextMenu onDelete={handleDelete} onEdit={handleEdit} />
            </div>

            <Spoiler hideLabel="Hide" maxHeight={400} showLabel="Show more">
                <div data-color-mode="light">
                    <MDEditor.Markdown
                        className="[&_pre>code]:whitespace-break-spaces! [&_p]:mb-1!"
                        source={attribute.description ?? ''}
                        style={{
                            backgroundColor: 'transparent',
                            fontSize: '0.85rem'
                        }}
                    />
                </div>
            </Spoiler>

            <form
                autoComplete="off"
                className="flex flex-col gap-3"
                onSubmit={formik.handleSubmit}
            >
                <AttributeValue isInline formik={formik} />
                <Button
                    className="self-end"
                    disabled={
                        !formik.dirty || !formik.isValid || formik.isSubmitting
                    }
                    size="xs"
                    type="submit"
                >
                    Save
                </Button>
            </form>
        </article>
    );
};

export default Attribute;
